/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import Plot from "react-plotly.js";
import {
    Alert,
    Button,
    Col,
    FormGroup,
    Input,
    InputGroup,
    InputGroupText,
    Label,
    Row,
    PopoverBody,
    Table,
    UncontrolledPopover,
} from "reactstrap";
import {
    RipeningProgram,
    RipeningProgramInput,
    RipewisePhase,
    RipewiseSettings,
} from "../../../../hooks/useRipeningPrograms";
import {
    DEFAULT_PHASE_STATES,
    isPhaseEnabled,
    PHASE_SLOTS,
    PHASE_STATE_LABELS,
    PhaseState,
    phaseHeading,
    phaseSlotLabel,
    phaseSlotName,
    phaseStateOf,
} from "../../../../constants/ripeningPhaseSlots";
import {
    GENERAL_FIELD_KEYS,
    injectsN2,
    PHASE_FIELD_KEYS,
    PROGRAM_FIELDS,
    ProgramFieldDefinition,
    withDerivedEthyleneDosing,
} from "../../../../constants/ripeningProgramFields";
import {
    defaultStoreValues,
    isStorageDefinition,
    PROGRAM_KIND_LABELS,
    ProgramKind,
    programKindOf,
    STORE_LOW_OXYGEN,
    storageDefinition,
    storeInjectsN2,
    storeValuesOf,
    validateStoreValues,
} from "../../../../constants/ripeningStorePrograms";
import StoreSettingsFields from "../../Ripening/Helpers/StoreSettingsFields";
import { FRUIT_LIMIT_KEYS, fruitLimitsOf, FruitLimitKey, FruitLimits, RipewiseConfig } from "../../../../actions/Tenants/config/ripewise";

export const defaultRipewiseSettings = (): RipewiseSettings => ({
    phases: PHASE_SLOTS
        .filter((slot) => DEFAULT_PHASE_STATES[slot] !== "do_not_use")
        .map((slot): RipewisePhase => ({ phase: slot, values: DEFAULT_PHASE_STATES[slot] === "disabled" ? { Enable: 0 } : {} })),
    general: {},
});

/**
 * Phases in slot order, each slot at most once; slots outside 1..7 are dropped.
 * Ethylene dosing follows the C2H4 setpoint, also in a program saved before it did.
 * Each phase carries its slot name, which the controller shows as the phase name.
 */
const normalizePhases = (phases: RipewiseSettings["phases"] = []) => {
    const bySlot = new Map<number, RipewisePhase>();
    phases.forEach((phase) => {
        if (PHASE_SLOTS.includes(phase.phase) && !bySlot.has(phase.phase)) {
            bySlot.set(phase.phase, {
                phase: phase.phase,
                name: phaseSlotName(phase.phase),
                values: withDerivedEthyleneDosing(phase.values),
            });
        }
    });
    return Array.from(bySlot.values()).sort((a, b) => a.phase - b.phase);
};

/** Each phase after the first starts at the previous phase's end temperature. */
const chainStartTemperatures = (phases: RipewisePhase[]): RipewisePhase[] => phases.map((phase, index) => {
    if (index === 0) return phase;
    const values = { ...phase.values };
    const previousEnd = phases[index - 1].values.T_End;
    if (previousEnd === undefined) delete values.T_Start;
    else values.T_Start = previousEnd;
    return { ...phase, values };
});

// * Only enabled phases run, so only they count towards the program's duration.
const calculateDurationMinutes = (phases: RipewisePhase[] = []) => Math.round(
    phases.filter(isPhaseEnabled).reduce((total, phase) => total + (Number(phase.values.TimeSpan) || 0), 0) * 60,
);

export const defaultRipeningProgram = (): RipeningProgramInput => {
    const ripewise = defaultRipewiseSettings();
    return {
        name: "",
        fruit_type: "",
        duration_minutes: calculateDurationMinutes(ripewise.phases),
        active: true,
        definition: {
            mode: "ripewise",
            storage: null,
            ripewise,
        },
    };
};

export const cloneRipeningProgram = (program: RipeningProgram): RipeningProgramInput => {
    // * A storage program, also one saved in the older storage mode, is edited as its store settings.
    if (isStorageDefinition(program.definition)) {
        return {
            name: program.name,
            fruit_type: program.fruit_type,
            duration_minutes: program.duration_minutes,
            active: program.active,
            definition: storageDefinition(storeValuesOf(program.definition)),
        };
    }
    const ripewise = program.definition.ripewise ? {
        ...program.definition.ripewise,
        phases: normalizePhases(program.definition.ripewise.phases),
        general: { ...program.definition.ripewise.general },
    } : null;
    return {
        name: program.name,
        fruit_type: program.fruit_type,
        duration_minutes: program.definition.mode === "ripewise" && ripewise
            ? calculateDurationMinutes(ripewise.phases)
            : program.duration_minutes,
        active: program.active,
        definition: {
            mode: program.definition.mode,
            storage: program.definition.storage ? { ...program.definition.storage } : null,
            ripewise,
        },
    };
};

// * A storage program runs until it is stopped; its duration only plans the cycle's end date.
export const DEFAULT_STORAGE_DURATION_MINUTES = 24 * 60;

/** The same program switched to ripening (default phases) or storage (default store settings). */
export function withProgramKind(program: RipeningProgramInput, kind: ProgramKind): RipeningProgramInput {
    if (programKindOf(program.definition) === kind) return program;
    if (kind === "storage") {
        return {
            ...program,
            duration_minutes: program.duration_minutes >= 1 ? program.duration_minutes : DEFAULT_STORAGE_DURATION_MINUTES,
            definition: storageDefinition(defaultStoreValues()),
        };
    }
    const { definition, duration_minutes: durationMinutes } = defaultRipeningProgram();
    return { ...program, definition, duration_minutes: durationMinutes };
}

/** The program as it is saved: phases in slot order with ethylene dosing following the C2H4 setpoint. */
export const normalizeRipeningProgram = (program: RipeningProgramInput): RipeningProgramInput => {
    if (isStorageDefinition(program.definition)) {
        return { ...program, definition: storageDefinition(storeValuesOf(program.definition)) };
    }
    const ripewise = program.definition.ripewise;
    if (!ripewise) return program;
    return {
        ...program,
        definition: { ...program.definition, ripewise: { ...ripewise, phases: normalizePhases(ripewise.phases) } },
    };
};

// * The room's controller shows "<name> - <history row number>" in at most 40 characters:
// * 30 for the name leaves room for " - " and a row number of up to 7 digits.
export const PROGRAM_NAME_MAX_LENGTH = 30;

export function validateRipeningProgram(program: RipeningProgramInput): string | null {
    if (!program.name.trim()) return "Name is required.";
    if (program.name.trim().length > PROGRAM_NAME_MAX_LENGTH) {
        return `Name can be at most ${PROGRAM_NAME_MAX_LENGTH} characters, so it fits on the ripening room's display.`;
    }
    if (!program.fruit_type.trim()) return "Fruit type is required.";
    if (!Number.isInteger(program.duration_minutes) || program.duration_minutes < 1) {
        return "Duration must be a positive number of minutes.";
    }
    if (isStorageDefinition(program.definition)) return validateStoreValues(storeValuesOf(program.definition));
    if (program.definition.mode === "ripewise") {
        const phases = normalizePhases(program.definition.ripewise?.phases ?? []);
        if (!phases.some(isPhaseEnabled)) return "Enable at least one phase.";
        let previousEnd: number | undefined;
        let phaseError: string | null = null;
        phases.forEach((phase) => {
            if (phaseError) return;
            // * A disabled phase can be switched on during a cycle, so it needs complete settings too.
            const values = phase.values;
            const label = phaseSlotLabel(phase.phase);
            const duration = values.TimeSpan;
            const startTemperature = values.T_Start;
            const endTemperature = values.T_End;
            if (duration === undefined || !Number.isFinite(duration) || duration <= 0) {
                phaseError = `${label} needs a positive ${PROGRAM_FIELDS.TimeSpan.label.toLowerCase()}, or set it to ${PHASE_STATE_LABELS.do_not_use}.`;
                return;
            }
            if (startTemperature === undefined || !Number.isFinite(startTemperature)
                || endTemperature === undefined || !Number.isFinite(endTemperature)) {
                phaseError = `${label} needs both a start and an end temperature.`;
                return;
            }
            if (values.Modus_Aereation === undefined || !Number.isFinite(values.Modus_Aereation)) {
                phaseError = `${label} needs a ventilation mode.`;
                return;
            }
            if (previousEnd !== undefined && startTemperature !== previousEnd) {
                phaseError = `${label} start temperature must match the previous phase end temperature.`;
                return;
            }
            previousEnd = endTemperature;
        });
        if (phaseError) return phaseError;
    }
    return null;
}

const LOW_OXYGEN_CELL = "Low oxygen: the room is dangerous to enter while this phase runs.";

/** "2", "2 and 4", "2, 3 and 4". */
const formatList = (items: Array<string | number>) => (items.length < 2
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`);

/** The warning for phases that inject N2, or null when none does. */
export function lowOxygenWarning(phases: RipewisePhase[]): string | null {
    const slots = normalizePhases(phases).filter((phase) => injectsN2(phase.values)).map((phase) => phase.phase);
    if (slots.length === 0) return null;
    return `N2 injection is on in phase${slots.length > 1 ? "s" : ""} ${formatList(slots)}. The room is dangerous to enter while it runs.`;
}

function formatPhaseDuration(minutes: number) {
    if (!Number.isFinite(minutes) || minutes <= 0) return "0 hours";
    const hours = Math.round((minutes / 60) * 100) / 100;
    const days = Math.round((hours / 24) * 100) / 100;
    if (days >= 1) return `${hours} hours (${days} day${days === 1 ? "" : "s"})`;
    return `${hours} hours`;
}

type PhaseFieldKey = typeof PHASE_FIELD_KEYS[number];
type GeneralFieldKey = typeof GENERAL_FIELD_KEYS[number];

// * The phase grid: duration, then what is switched on, then the values it works towards.
// * Blocks are set apart by a thicker line, not by extra rows. Ethylene dosing has no row:
// * it follows the C2H4 setpoint.
export const PHASE_FIELD_BLOCKS: PhaseFieldKey[][] = [
    ["TimeSpan"],
    ["Modus_Scrubber", "Modus_N2", "Modus_Aereation"],
    ["T_Start", "T_End", "Spv_C2H4", "Spv_CO2", "Spv_O2"],
];

// * Short row names for this grid only, without the unit the input already shows; other screens keep the shared field labels.
const GRID_ROW_LABELS: Partial<Record<PhaseFieldKey, string>> = {
    TimeSpan: "Duration",
    T_Start: "Start temp",
    T_End: "End temp",
    Spv_C2H4: "C2H4",
    Spv_CO2: "CO2",
    Spv_O2: "O2",
};

// * General settings, grouped: fan and ventilation, then the limits with each min next to its max.
const GENERAL_GROUPS: Array<{ title: string; width: number; fields: Array<[GeneralFieldKey, string]> }> = [
    {
        title: "Fan & ventilation",
        width: 5,
        fields: [["FanSpeed", "Fan speed"], ["Aereation_Period", "Ventilation period"], ["Aereation_Interval", "Ventilation interval"]],
    },
    {
        title: "Limits",
        width: 7,
        fields: [["T_Air_Min", "Air min"], ["T_Air_Max", "Air max"], ["T_Prod_Min", "Product min"], ["T_Prod_Max", "Product max"]],
    },
];

export interface ProgramWarning {
    // * null: a general setting.
    phase: number | null;
    field: string;
    message: string;
}

const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/** Settings that are allowed but probably not meant; they never block saving. */
export function programWarnings(program: RipeningProgramInput): ProgramWarning[] {
    const ripewise = program.definition.ripewise;
    if (program.definition.mode !== "ripewise" || !ripewise) return [];
    const warnings: ProgramWarning[] = [];
    const general = ripewise.general ?? {};
    const add = (phase: number | null, field: string, message: string) => warnings.push({ phase, field, message });

    const range = (minKey: string, maxKey: string, what: string) => {
        const min = general[minKey];
        const max = general[maxKey];
        if (isNumber(min) && isNumber(max) && min > max) add(null, minKey, `${what} minimum (${min} °C) is above its maximum (${max} °C).`);
    };
    range("T_Prod_Min", "T_Prod_Max", "Product temperature");
    range("T_Air_Min", "T_Air_Max", "Air temperature");
    const period = general.Aereation_Period;
    const interval = general.Aereation_Interval;
    if (isNumber(period) && isNumber(interval) && interval > 0 && period > interval) {
        add(null, "Aereation_Period", `Ventilation period (${period} min) is longer than its interval (${interval} min).`);
    }

    normalizePhases(ripewise.phases).forEach((phase) => {
        const values = phase.values;
        const name = phaseSlotLabel(phase.phase);
        // * The controller regulates on the setpoints itself; ethylene dosing, the CO2 scrubber and
        // * N2 control are extra methods, so a setpoint is never checked against them.
        if (isNumber(values.Spv_O2) && values.Spv_O2 > 21) {
            add(phase.phase, "Spv_O2", `${name}: an O2 setpoint of ${values.Spv_O2}% is above air (21%) and cannot be reached.`);
        }
        (["T_Start", "T_End"] as const).forEach((key) => {
            const temperature = values[key];
            if (!isNumber(temperature)) return;
            const label = key === "T_Start" ? "start" : "end";
            const checks: Array<[string, string, "min" | "max"]> = [
                ["T_Prod_Min", "product minimum", "min"], ["T_Prod_Max", "product maximum", "max"],
                ["T_Air_Min", "air minimum", "min"], ["T_Air_Max", "air maximum", "max"],
            ];
            const broken = checks.find(([limitKey, , side]) => {
                const limit = general[limitKey];
                return isNumber(limit) && (side === "min" ? temperature < limit : temperature > limit);
            });
            if (broken) {
                const [limitKey, limitName, side] = broken;
                add(phase.phase, key, `${name}: ${label} temperature ${temperature} °C is ${side === "min" ? "below" : "above"} the ${limitName} (${general[limitKey]} °C).`);
            }
        });
    });
    return warnings;
}

// * Each trace and the axis it sits on: temperature left, gases right so neither squashes the other.
const CHART_SERIES = [
    { id: "temperature", fields: ["T_Start", "T_End"], label: "Temperature (°C)", color: "#0d6efd", axis: "y" },
    { id: "Spv_C2H4", fields: ["Spv_C2H4"], label: PROGRAM_FIELDS.Spv_C2H4.label, color: "#d63384", axis: "y2" },
    { id: "Spv_O2", fields: ["Spv_O2"], label: PROGRAM_FIELDS.Spv_O2.label, color: "#198754", axis: "y3" },
    { id: "Spv_CO2", fields: ["Spv_CO2"], label: PROGRAM_FIELDS.Spv_CO2.label, color: "#fd7e14", axis: "y3" },
] as const;

const CHART_AXES: Record<string, Record<string, any>> = {
    y: { title: { text: "Temperature (°C)" }, side: "left" },
    y2: { title: { text: "C2H4 (ppm)" }, side: "right", overlaying: "y", rangemode: "tozero", showgrid: false },
    y3: { title: { text: "O2 / CO2 / fan (%)" }, side: "right", overlaying: "y", anchor: "free", position: 1, rangemode: "tozero", showgrid: false },
};

// Fields whose input focus isolates their trace in the chart.
const CHART_FOCUS: Record<string, string> = {
    T_Start: "temperature", T_End: "temperature", Spv_C2H4: "Spv_C2H4", Spv_O2: "Spv_O2", Spv_CO2: "Spv_CO2", FanSpeed: "FanSpeed",
};

export interface SetpointChart {
    data: Array<Record<string, any>>;
    layout: Record<string, any>;
    // * Disabled phases are not part of the planned run, so they are listed instead of drawn.
    notInRun: string[];
}

/** The planned run of enabled phases: temperature ramps on the left axis, gases on the right. */
export function buildSetpointChart(
    phases: RipewisePhase[],
    general: Record<string, number> | undefined,
    focusedSeries: string | null = null,
): SetpointChart {
    const ordered = normalizePhases(phases);
    const running = ordered.filter(isPhaseEnabled);
    const notInRun = ordered.filter((phase) => !isPhaseEnabled(phase)).map((phase) => phaseSlotLabel(phase.phase));
    const spans: Array<{ phase: RipewisePhase; start: number; end: number }> = [];
    let elapsed = 0;
    running.forEach((phase) => {
        const duration = Number(phase.values.TimeSpan);
        if (!Number.isFinite(duration) || duration <= 0) return;
        spans.push({ phase, start: elapsed, end: elapsed + duration });
        elapsed += duration;
    });
    const total = elapsed;

    const traces: Array<{ id: string; axis: string; trace: Record<string, any> }> = [];
    CHART_SERIES.forEach((series) => {
        const x: number[] = [];
        const y: Array<number | null> = [];
        spans.forEach(({ phase, start, end }) => {
            const values = phase.values;
            if (series.id === "temperature") {
                // * The temperature ramps from start to end over the phase.
                const from = values.T_Start;
                const to = values.T_End;
                if (!isNumber(from) && !isNumber(to)) return;
                x.push(start, end);
                y.push(isNumber(from) ? from : (to as number), isNumber(to) ? to : (from as number));
                return;
            }
            // * The setpoint holds whatever extra method is on: draw it in every phase that sets it.
            const setpoint = values[series.fields[0]];
            x.push(start, end);
            y.push(isNumber(setpoint) ? setpoint : null, isNumber(setpoint) ? setpoint : null);
        });
        if (!y.some((value) => value !== null)) return;
        traces.push({
            id: series.id,
            axis: series.axis,
            trace: {
                x,
                y,
                type: "scatter",
                mode: "lines+markers",
                name: series.label,
                connectgaps: false,
                line: { color: series.color, ...(series.id === "temperature" ? {} : { shape: "hv" }) },
                marker: { size: 5 },
            },
        });
    });
    const fanSpeed = general?.FanSpeed;
    if (isNumber(fanSpeed) && total > 0) {
        traces.push({
            id: "FanSpeed",
            axis: "y3",
            trace: {
                x: [0, total],
                y: [fanSpeed, fanSpeed],
                type: "scatter",
                mode: "lines",
                name: PROGRAM_FIELDS.FanSpeed.label,
                line: { color: "#6c757d", dash: "dash" },
            },
        });
    }

    const visible = focusedSeries ? traces.filter((item) => item.id === focusedSeries) : traces;
    const usedAxes = Array.from(new Set(traces.map((item) => item.axis)));
    const layoutAxes: Record<string, any> = {};
    usedAxes.forEach((axis) => {
        const layoutKey = axis === "y" ? "yaxis" : `yaxis${axis.slice(1)}`;
        const config = { ...CHART_AXES[axis] };
        // * Without a temperature axis the gas axes have nothing to overlay.
        if (!usedAxes.includes("y")) delete config.overlaying;
        layoutAxes[layoutKey] = config;
    });

    // * Phase names along the time axis; a short phase gets its number, the full name on hover.
    const shapes = spans.slice(1).map(({ start }) => ({
        type: "line",
        xref: "x",
        yref: "paper",
        x0: start,
        x1: start,
        y0: 0,
        y1: 1,
        line: { color: "#adb5bd", width: 1, dash: "dot" },
    }));
    const annotations = spans.map(({ phase, start, end }) => {
        const wide = total > 0 && (end - start) / total >= 0.08;
        return {
            x: (start + end) / 2,
            xref: "x",
            y: 1.02,
            yref: "paper",
            yanchor: "bottom",
            showarrow: false,
            text: wide ? phaseSlotName(phase.phase) : `P${phase.phase}`,
            hovertext: phaseSlotLabel(phase.phase),
            font: { size: 11, color: "#495057" },
        };
    });

    return {
        data: visible.map(({ axis, trace }) => ({ ...trace, yaxis: axis })),
        layout: {
            autosize: true,
            height: 340,
            margin: { l: 55, r: 110, t: 40, b: 45 },
            xaxis: { title: { text: "Hours" }, rangemode: "tozero", domain: [0, usedAxes.includes("y3") ? 0.9 : 1] },
            ...layoutAxes,
            shapes,
            annotations,
            legend: { orientation: "h", y: -0.2 },
            hovermode: "x unified",
        },
        notInRun,
    };
}

interface RipeningProgramFormProps {
    value: RipeningProgramInput;
    fruitTypes?: Array<{ value: string; text: string }>;
    // * The tenant's temperature limits per fruit (RipeWise config): prefilled and read-only.
    fruitLimits?: RipewiseConfig["fruit_limits"];
    onChange: (value: RipeningProgramInput) => void;
}

const COLUMN_WIDTH = 150;

// * Room around text and inputs: nothing sits against a cell line, neighbouring inputs keep a gap.
const phaseTableCss = css`
    && > :not(caption) > * > * { padding: 0.35rem 0.5rem; }
    th { white-space: nowrap; }
`;

// * What the phase states mean, on request instead of always on screen.
const PHASE_STATE_HELP: Array<[string, string]> = [
    ["Enabled", "the phase runs."],
    ["Disabled", "kept with its settings; it can be switched on during a cycle."],
    ["Do not use", "not part of the program; it cannot be switched on during a cycle."],
];

export default function RipeningProgramForm({ value, fruitTypes = [], fruitLimits, onChange }: RipeningProgramFormProps) {
    const hasCurrentFruitType = fruitTypes.some((fruitType) => fruitType.value === value.fruit_type);
    const [focusedChartSeries, setFocusedChartSeries] = useState<string | null>(null);
    const lockedLimits = fruitLimitsOf(fruitLimits, value.fruit_type);
    const isLocked = (field: string) => field in lockedLimits;

    // * Keep the configured limits of the fruit in the program, also after the fruit type changes,
    // * and clear a field's forced value once its fruit type no longer locks it (instead of leaving it stale).
    const prevLockedLimitsRef = useRef<FruitLimits>({});
    useEffect(() => {
        const ripewise = value.definition.ripewise;
        if (value.definition.mode !== "ripewise" || !ripewise) return;
        const general = ripewise.general ?? {};
        const prevLockedLimits = prevLockedLimitsRef.current;
        prevLockedLimitsRef.current = lockedLimits;

        const nextGeneral = { ...general };
        let changed = false;
        FRUIT_LIMIT_KEYS.forEach((key) => {
            if (key in lockedLimits) {
                const lockedValue = lockedLimits[key] as number;
                if (nextGeneral[key] !== lockedValue) {
                    nextGeneral[key] = lockedValue;
                    changed = true;
                }
            } else if (key in prevLockedLimits) {
                delete nextGeneral[key];
                changed = true;
            }
        });
        if (changed) setRipewise({ ...ripewise, general: nextGeneral });
    }, [value.fruit_type, value.definition.mode, value.definition.ripewise?.general, JSON.stringify(lockedLimits)]);

    const updateField = (event: ChangeEvent<HTMLInputElement>) => {
        const { name, value: inputValue, checked, type } = event.target;
        let nextValue: string | number | boolean = inputValue;
        if (type === "checkbox") nextValue = checked;
        else if (name === "duration_minutes") nextValue = Number(inputValue);
        onChange({
            ...value,
            [name]: nextValue,
        });
    };

    const setRipewise = (settings: RipewiseSettings) => onChange({
        ...value,
        duration_minutes: calculateDurationMinutes(settings.phases),
        definition: { ...value.definition, ripewise: { ...settings, phases: normalizePhases(settings.phases) } },
    });

    const setGeneralValue = (field: string, inputValue: string) => {
        const ripewise = value.definition.ripewise ?? defaultRipewiseSettings();
        const values = { ...ripewise.general };
        if (inputValue === "") delete values[field];
        else values[field] = Number(inputValue);
        setRipewise({ ...ripewise, general: values });
    };

    const setPhaseValue = (phaseNumber: number, field: string, inputValue: string) => {
        const ripewise = value.definition.ripewise ?? defaultRipewiseSettings();
        const phases = normalizePhases(ripewise.phases);
        const numericValue = inputValue === "" ? undefined : Number(inputValue);
        const phaseIndex = phases.findIndex((phase) => phase.phase === phaseNumber);
        const nextPhases = phases.map((phase, index) => {
            if (index !== phaseIndex) return phase;
            const values = { ...phase.values };
            if (numericValue === undefined) delete values[field];
            else values[field] = numericValue;
            return { ...phase, values };
        });
        // Propagate T_End into the next phase's read-only T_Start so the ramp stays continuous.
        if (field === "T_End" && phaseIndex >= 0 && phaseIndex + 1 < nextPhases.length) {
            const nextValues = { ...nextPhases[phaseIndex + 1].values };
            if (numericValue === undefined) delete nextValues.T_Start;
            else nextValues.T_Start = numericValue;
            nextPhases[phaseIndex + 1] = { ...nextPhases[phaseIndex + 1], values: nextValues };
        }
        setRipewise({ ...ripewise, phases: nextPhases });
    };

    const setPhaseState = (slot: number, state: PhaseState) => {
        const ripewise = value.definition.ripewise ?? defaultRipewiseSettings();
        const phases = normalizePhases(ripewise.phases);
        const existing = phases.find((phase) => phase.phase === slot);
        let nextPhases: RipewisePhase[];
        if (state === "do_not_use") {
            nextPhases = phases.filter((phase) => phase.phase !== slot);
        } else {
            const values = { ...existing?.values };
            if (state === "disabled") values.Enable = 0;
            else delete values.Enable;
            nextPhases = [...phases.filter((phase) => phase.phase !== slot), { phase: slot, values }]
                .sort((a, b) => a.phase - b.phase);
        }
        setRipewise({ ...ripewise, phases: chainStartTemperatures(nextPhases) });
    };

    const kind = programKindOf(value.definition);
    const storeValues = storeValuesOf(value.definition);
    const setStoreValue = (field: string, fieldValue: number | undefined) => {
        const store = { ...storeValues };
        if (fieldValue === undefined) delete store[field];
        else store[field] = fieldValue;
        onChange({ ...value, definition: storageDefinition(store) });
    };

    const phases = normalizePhases(value.definition.ripewise?.phases ?? []);
    const phaseBySlot = new Map(phases.map((phase) => [phase.phase, phase]));
    const stateCounts = PHASE_SLOTS.reduce<Record<PhaseState, number>>((counts, slot) => {
        counts[phaseStateOf(phaseBySlot.get(slot))] += 1;
        return counts;
    }, { enabled: 0, disabled: 0, do_not_use: 0 });
    const warnings = programWarnings(value);
    const warningsAt = (phase: number | null, field: string) => warnings.filter((warning) => warning.phase === phase && warning.field === field);
    const chart = buildSetpointChart(phases, value.definition.ripewise?.general, focusedChartSeries);
    const lowOxygen = lowOxygenWarning(phases);

    const focusHandlers = (field: string) => (CHART_FOCUS[field] ? {
        onFocus: () => setFocusedChartSeries(CHART_FOCUS[field]),
        onBlur: () => setFocusedChartSeries(null),
    } : {});

    const renderWarnings = (phase: number | null, field: string) => warningsAt(phase, field).map((warning) => (
        <div key={warning.message} className="small text-warning-emphasis mt-1" data-testid={`warning-${phase ?? "general"}-${field}`} title={warning.message}>
            {"⚠ "}{warning.message.replace(/^[^:]+: /, "")}
        </div>
    ));

    const renderSelect = (phase: RipewisePhase, field: PhaseFieldKey, className?: string) => {
        const definition: ProgramFieldDefinition = PROGRAM_FIELDS[field];
        const current = phase.values[field];
        const known = definition.options?.some((option) => option.value === current);
        return (
            <Input
                id={`phase-${phase.phase}-${field}`}
                bsSize="sm"
                type="select"
                className={className}
                aria-label={`${definition.label} phase ${phase.phase}`}
                title={definition.key}
                value={current ?? ""}
                onChange={(event) => setPhaseValue(phase.phase, field, event.target.value)}
            >
                <option value="">—</option>
                {current !== undefined && !known && <option value={current}>{current}</option>}
                {definition.options?.map((option) => (
                    <option key={option.value} value={option.value} title={`${option.value} — ${option.code ?? option.label}`}>
                        {option.label}
                    </option>
                ))}
            </Input>
        );
    };

    const renderNumber = (phase: RipewisePhase, field: PhaseFieldKey) => {
        const definition: ProgramFieldDefinition = PROGRAM_FIELDS[field];
        return (
            <InputGroup size="sm" className="flex-nowrap">
                <Input
                    id={`phase-${phase.phase}-${field}`}
                    type="number"
                    step={definition.step ?? "any"}
                    min={definition.min}
                    max={definition.max}
                    aria-label={`${definition.label} phase ${phase.phase}`}
                    title={definition.key}
                    value={phase.values[field] ?? ""}
                    onChange={(event) => setPhaseValue(phase.phase, field, event.target.value)}
                    {...focusHandlers(field)}
                />
                {definition.unit && <InputGroupText>{definition.unit}</InputGroupText>}
            </InputGroup>
        );
    };

    const renderPhaseCell = (field: PhaseFieldKey, phase: RipewisePhase) => {
        const definition: ProgramFieldDefinition = PROGRAM_FIELDS[field];
        // Only the first phase sets the starting temperature; later phases start where the previous one ended.
        if (field === "T_Start" && phases[0]?.phase !== phase.phase) {
            return (
                <>
                    <InputGroup size="sm" className="flex-nowrap">
                        <Input
                            aria-label={`${definition.label} phase ${phase.phase}`}
                            value={phase.values.T_Start ?? ""}
                            readOnly
                            disabled
                            title="The end temperature of the phase before"
                        />
                        <InputGroupText>{definition.unit}</InputGroupText>
                    </InputGroup>
                    <div className="small text-muted mt-1" data-testid={`start-from-previous-${phase.phase}`}>from previous phase</div>
                    {renderWarnings(phase.phase, field)}
                </>
            );
        }
        return (
            <>
                {definition.kind === "select" ? renderSelect(phase, field) : renderNumber(phase, field)}
                {field === "Modus_N2" && injectsN2(phase.values) && (
                    <div className="small fw-semibold text-warning-emphasis mt-1 text-nowrap" data-testid={`low-oxygen-${phase.phase}`} title={LOW_OXYGEN_CELL}>
                        {"⚠ "}Low oxygen
                    </div>
                )}
                {renderWarnings(phase.phase, field)}
            </>
        );
    };

    // * The first row of each block after the first gets a thicker top line.
    const blockStartCss = { borderTop: "2px solid var(--bs-gray-500, #adb5bd)" };

    return (
        <>
            <Row>
                <Col md="auto">
                    <FormGroup>
                        <Label for="program-mode">Mode</Label>
                        <Input
                            id="program-mode"
                            type="select"
                            value={kind}
                            onChange={(event) => onChange(withProgramKind(value, event.target.value as ProgramKind))}
                            style={{ width: "10rem" }}
                        >
                            {(Object.keys(PROGRAM_KIND_LABELS) as ProgramKind[]).map((option) => (
                                <option key={option} value={option}>{PROGRAM_KIND_LABELS[option]}</option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
                <Col md>
                    <FormGroup>
                        <Label for="program-name">Name</Label>
                        <Input
                            id="program-name"
                            name="name"
                            value={value.name}
                            onChange={updateField}
                            maxLength={Math.max(PROGRAM_NAME_MAX_LENGTH, value.name.length)}
                            invalid={value.name.trim().length > PROGRAM_NAME_MAX_LENGTH}
                            aria-describedby="program-name-count"
                        />
                        <div
                            id="program-name-count"
                            className={`small mt-1 ${value.name.trim().length > PROGRAM_NAME_MAX_LENGTH ? "text-danger" : "text-muted"}`}
                        >
                            {value.name.trim().length}/{PROGRAM_NAME_MAX_LENGTH} characters
                        </div>
                    </FormGroup>
                </Col>
                <Col md="auto">
                    <FormGroup>
                        <Label for="program-fruit-type">Fruit type</Label>
                        <Input id="program-fruit-type" name="fruit_type" type="select" value={value.fruit_type} onChange={updateField} style={{ width: "14rem" }}>
                            <option value="">Select a fruit type</option>
                            {!hasCurrentFruitType && value.fruit_type && (
                                <option value={value.fruit_type}>{value.fruit_type}</option>
                            )}
                            {fruitTypes.map((fruitType) => (
                                <option key={fruitType.value} value={fruitType.value}>
                                    {fruitType.text || fruitType.value}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
                <div className="w-100" />
                <Col md={4}>
                    {kind === "ripening" && value.definition.mode === "ripewise" ? (
                        <FormGroup>
                            <Label>Total duration</Label>
                            <Input value={formatPhaseDuration(value.duration_minutes)} readOnly disabled />
                        </FormGroup>
                    ) : (
                        <FormGroup>
                            {kind === "storage" ? (
                                <>
                                    <Label for="program-duration">Planned duration (hours)</Label>
                                    <Input
                                        id="program-duration"
                                        type="number"
                                        min={1}
                                        step="any"
                                        value={value.duration_minutes / 60}
                                        onChange={(event) => onChange({ ...value, duration_minutes: Math.round(Number(event.target.value) * 60) })}
                                    />
                                    <div className="small text-muted mt-1">The room stores until it is stopped; this plans the cycle end date.</div>
                                </>
                            ) : (
                                <>
                                    <Label for="program-duration">Total duration (minutes)</Label>
                                    <Input id="program-duration" name="duration_minutes" type="number" min={1} value={value.duration_minutes} onChange={updateField} />
                                </>
                            )}
                        </FormGroup>
                    )}
                </Col>
                <Col md={2} className="d-flex align-items-center">
                    <FormGroup check switch className="mt-3">
                        <Input id="program-active" name="active" type="checkbox" checked={value.active} onChange={updateField} />
                        <Label for="program-active" check>Active</Label>
                    </FormGroup>
                </Col>
            </Row>

            {kind === "storage" && (
                <div data-testid="storage-settings">
                    <h5 className="mb-2">Storage settings</h5>
                    {storeInjectsN2(storeValues) && (
                        <Alert color="warning" className="py-2 px-3 mb-2 small" data-testid="low-oxygen-warning">
                            {"⚠ "}<strong>Low oxygen:</strong> {STORE_LOW_OXYGEN}
                        </Alert>
                    )}
                    <StoreSettingsFields values={storeValues} onChange={setStoreValue} showKeys idPrefix="storage" />
                </div>
            )}

            {kind === "ripening" && value.definition.mode === "ripewise" && (
                <div className="d-flex flex-column gap-3">
                    <div data-testid="general-settings">
                        <h5 className="mb-2">General settings</h5>
                        <Row className="gx-5 gy-3">
                            {GENERAL_GROUPS.map((group) => (
                                <Col xl={group.width} key={group.title}>
                                    <div className="small fw-semibold text-muted mb-1" data-testid={`general-group-${group.title}`}>{group.title}</div>
                                    <Row className="gx-3 gy-2">
                                        {group.fields.map(([field, label]) => {
                                            const definition: ProgramFieldDefinition = PROGRAM_FIELDS[field];
                                            return (
                                                <Col xs={6} md={12 / group.fields.length} key={field}>
                                                    <Label for={`general-${field}`} className="small mb-1"><span title={definition.key}>{label}</span></Label>
                                                    <InputGroup size="sm" className="flex-nowrap">
                                                        <Input
                                                            id={`general-${field}`}
                                                            type="number"
                                                            step={definition.step ?? "any"}
                                                            min={definition.min}
                                                            max={definition.max}
                                                            title={isLocked(field) ? `Set for ${value.fruit_type} in the RipeWise config` : definition.key}
                                                            value={lockedLimits[field as FruitLimitKey] ?? value.definition.ripewise?.general?.[field] ?? ""}
                                                            disabled={isLocked(field)}
                                                            onChange={(event) => setGeneralValue(field, event.target.value)}
                                                            {...focusHandlers(field)}
                                                        />
                                                        {definition.unit && <InputGroupText>{definition.unit}</InputGroupText>}
                                                    </InputGroup>
                                                    {renderWarnings(null, field)}
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </Col>
                            ))}
                        </Row>
                    </div>

                    <div>
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-1">
                                <h5 className="mb-0">Phases</h5>
                                <Button
                                    id="phase-states-help"
                                    type="button"
                                    color="link"
                                    size="sm"
                                    className="p-0 ms-1 lh-1 text-decoration-none"
                                    aria-label="What do the phase states mean?"
                                >
                                    ?
                                </Button>
                                <UncontrolledPopover target="phase-states-help" trigger="legacy" placement="right">
                                    <PopoverBody data-testid="phase-states-help-body">
                                        {PHASE_STATE_HELP.map(([state, meaning]) => (
                                            <div key={state}><strong>{state}</strong>: {meaning}</div>
                                        ))}
                                    </PopoverBody>
                                </UncontrolledPopover>
                            </div>
                            <span className="text-muted small" data-testid="phase-state-counts">
                                {stateCounts.enabled} enabled · {stateCounts.disabled} disabled · {stateCounts.do_not_use} not used
                            </span>
                        </div>
                        {lowOxygen && (
                            <Alert color="warning" className="py-2 px-3 mb-0 mt-2 small" data-testid="low-oxygen-warning">
                                {"⚠ "}<strong>Low oxygen:</strong> {lowOxygen}
                            </Alert>
                        )}
                        <div className="table-responsive">
                            <Table bordered size="sm" className="align-top mb-0 mt-2" css={phaseTableCss}>
                                <thead>
                                    <tr>
                                        <th style={{ minWidth: 150 }}>Phase state</th>
                                        {PHASE_SLOTS.map((slot) => {
                                            const state = phaseStateOf(phaseBySlot.get(slot));
                                            return (
                                                <th
                                                    key={slot}
                                                    data-testid={`phase-slot-${slot}`}
                                                    data-state={state}
                                                    style={{ minWidth: state === "do_not_use" ? 110 : COLUMN_WIDTH }}
                                                    className={state === "disabled" ? "bg-body-tertiary" : undefined}
                                                >
                                                    <div className={state === "enabled" ? undefined : "text-muted"}>{phaseHeading(slot).title}</div>
                                                    {phaseHeading(slot).subtitle && <div className="small text-muted fw-normal">{phaseHeading(slot).subtitle}</div>}
                                                    <Input
                                                        bsSize="sm"
                                                        type="select"
                                                        className="mt-1"
                                                        aria-label={`State phase ${slot}`}
                                                        value={state}
                                                        onChange={(event) => setPhaseState(slot, event.target.value as PhaseState)}
                                                    >
                                                        {(Object.keys(PHASE_STATE_LABELS) as PhaseState[]).map((option) => (
                                                            <option key={option} value={option}>{PHASE_STATE_LABELS[option]}</option>
                                                        ))}
                                                    </Input>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {PHASE_FIELD_BLOCKS.flatMap((block, blockIndex) => block.map((field, rowIndex) => {
                                        const rowStyle = blockIndex > 0 && rowIndex === 0 ? blockStartCss : undefined;
                                        return (
                                            <tr key={field} data-testid={`phase-row-${field}`} data-block-start={rowStyle ? "true" : undefined}>
                                                <th className="fw-semibold text-nowrap" style={rowStyle}>
                                                    <span title={PROGRAM_FIELDS[field].key}>{GRID_ROW_LABELS[field] ?? PROGRAM_FIELDS[field].label}</span>
                                                    {field === "Spv_C2H4" && (
                                                        <div className="small text-muted fw-normal" data-testid="ethylene-dosing-note" title={PROGRAM_FIELDS.Modus_C2H4.key}>
                                                            dosing on above 0
                                                        </div>
                                                    )}
                                                </th>
                                                {PHASE_SLOTS.map((slot) => {
                                                    const phase = phaseBySlot.get(slot);
                                                    if (!phase) {
                                                        return <td key={slot} className="text-muted text-center" style={rowStyle}>—</td>;
                                                    }
                                                    const lowOxygenCell = field === "Modus_N2" && injectsN2(phase.values);
                                                    let cellClass: string | undefined;
                                                    if (lowOxygenCell) cellClass = "table-warning";
                                                    else if (!isPhaseEnabled(phase)) cellClass = "bg-body-tertiary";
                                                    return (
                                                        <td
                                                            key={slot}
                                                            style={{ minWidth: COLUMN_WIDTH, ...rowStyle }}
                                                            className={cellClass}
                                                        >
                                                            {renderPhaseCell(field, phase)}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    }))}
                                </tbody>
                            </Table>
                        </div>
                        <div className="mt-3">
                            <h6>Setpoints over time</h6>
                            <Plot
                                data={chart.data}
                                layout={chart.layout}
                                config={{ responsive: true, displayModeBar: false }}
                                style={{ width: "100%" }}
                                useResizeHandler
                            />
                            {chart.notInRun.length > 0 && (
                                <div className="small text-muted" data-testid="chart-not-in-run">
                                    Not in the planned run (disabled, can be switched on during a cycle): {chart.notInRun.join(", ")}
                                </div>
                            )}
                        </div>
                    </div>

                    {warnings.length > 0 && (
                        <Alert color="warning" className="mb-0" data-testid="program-warnings">
                            <div className="fw-semibold mb-1">Check before saving ({warnings.length})</div>
                            <ul className="mb-0 ps-3 small">
                                {warnings.map((warning) => <li key={warning.message}>{warning.message}</li>)}
                            </ul>
                        </Alert>
                    )}
                </div>
            )}
        </>
    );
}
